import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { AlertFactory } from 'alerts/Alert'
import ModalViewModel, {
  ModalViewModelClosingSignal,
} from 'infra/ModalViewModel'

/**
 * Dialog for adding a note to a job.
 */
export default class AddJobNoteDialogViewModel extends ModalViewModel {
  @observable composerText = ''

  // Mirrors the body limit from job note service.
  readonly MAX_NOTE_LENGTH = 2000

  constructor(
    private readonly createNote: (body: string) => Promise<unknown>,
    private readonly newAlert: AlertFactory,
    closingSignal?: ModalViewModelClosingSignal,
  ) {
    super(closingSignal)
    makeObservable(this)
  }

  /**
   * Whether the note can be sent.
   */
  @computed
  get canSend() {
    return this.composerText.trim().length > 0 && !this.sendNote.pending
  }

  /**
   * Characters remaining before the note hits the max length.
   */
  @computed
  get noteCharsRemaining() {
    return this.composerText
      ? this.MAX_NOTE_LENGTH - this.composerText.length
      : this.MAX_NOTE_LENGTH
  }

  /**
   * Shows the dialog with a fresh composer.
   */
  show() {
    if (this.showing) {
      return
    }

    this.composerText = ''
    super.show()
  }

  @action.bound
  setComposerText(text: string) {
    this.composerText = text
  }

  /**
   * Creates a note and closes the dialog.
   */
  sendNote = task.resolved(async () => {
    const text = this.composerText.trim()
    if (!text) {
      return
    }

    try {
      await this.createNote(text)
      this.close()
    } catch (err) {
      this.newAlert().fromError(err).show()
    }
  })
}
